import { readDisabled, writeDisabled } from "../disabling";
import { formProductKey } from "../keys";
import {
	backgroundContrastRefusal,
	borderRadiusRefusal,
	contrastRefusal,
	getEffectiveBackgroundColor,
	getThemeBackgroundColor,
	getThemeTextColor,
	getUnderlyingBackgroundColor,
	hasInlineStyle,
	isEmptyString,
	notationRefusal,
} from "../validations";
import type { CustomizationHandler } from "./types";

/**
 * Why the label colour and the background `options` leave on `element` would
 * be unreadable together, or `null` when they are readable — or when this
 * call sends no colour.
 *
 * The pair is judged as it will be after this call: a colour sent is the new
 * one, a colour cleared with `""` is the theme's (the inline value lifted),
 * and a colour not sent is the current one. Clearing one colour of a pair an
 * app had set therefore still has to leave the label readable next to the
 * other. When neither colour stays inline, the element is back to the
 * theme's own pair, which is not the SDK's to judge.
 *
 * The refusal is reported on the colour this call sets — the background when
 * both are set — or else on the one it clears.
 */
function colorPairRefusal(
	element: Element,
	options: { fontColor?: string; backgroundColor?: string },
): string | null {
	const sendsFont = options.fontColor !== undefined;
	const sendsBackground = options.backgroundColor !== undefined;
	if (!sendsFont && !sendsBackground) return null;

	const fontColor = options.fontColor || undefined;
	const backgroundColor = options.backgroundColor || undefined;

	// A notation the contrast check cannot read is refused on its own, before
	// any measurement: one it cannot evaluate cannot be shown to be readable.
	if (fontColor !== undefined) {
		const refusal = notationRefusal(fontColor);
		if (refusal) return `refused "fontColor": ${refusal}`;
	}

	const fontStaysInline =
		fontColor !== undefined || (!sendsFont && hasInlineStyle(element, "color"));
	const backgroundStaysInline =
		backgroundColor !== undefined ||
		(!sendsBackground && hasInlineStyle(element, "background-color"));
	if (!fontStaysInline && !backgroundStaysInline) return null;

	const foreground =
		fontColor ??
		(sendsFont ? getThemeTextColor(element) : getComputedStyle(element).color);

	const blamed =
		backgroundColor !== undefined || !sendsFont
			? "backgroundColor"
			: "fontColor";
	const clearing = options[blamed] === "";

	const refusal =
		backgroundColor !== undefined
			? backgroundContrastRefusal(
					backgroundColor,
					foreground,
					getUnderlyingBackgroundColor(element),
				)
			: contrastRefusal(
					foreground,
					sendsBackground
						? getThemeBackgroundColor(element)
						: getEffectiveBackgroundColor(element),
				);
	if (!refusal) return null;
	return clearing
		? `refused "${blamed}": clearing it would leave the label unreadable: ${refusal}`
		: `refused "${blamed}": ${refusal}`;
}

/**
 * The storefront's add-to-cart button.
 *
 * `.js-addtocart` is in 29 of the 40 Core themes, as an
 * `<input type="submit">` in most of them and a `<button>` in some (e.g.
 * `flex`), hence the per-instance narrowing below.
 *
 * The button renders on the product page, on grid cards and in the quick
 * shop, each adding a different product, so a resolver gets the product of
 * each button, which `identify` maps back through the form it submits.
 *
 * @see ./README.md
 */
export const addToCartButton: CustomizationHandler<"add-to-cart-button"> = {
	host: "storefront",
	selector: ".js-addtocart",

	read(element) {
		return {
			text:
				element instanceof HTMLInputElement
					? element.value
					: element.textContent ?? "",
			// The inline styles, so restoring `""` hands the element back to the
			// theme instead of pinning a style the theme never set.
			fontColor: element instanceof HTMLElement ? element.style.color : "",
			backgroundColor:
				element instanceof HTMLElement ? element.style.backgroundColor : "",
			borderRadius:
				element instanceof HTMLElement ? element.style.borderRadius : "",
			// The SDK's own state, never the theme's `disabled` attribute: see
			// ../disabling.ts.
			disabled: readDisabled(element),
		};
	},

	operation(element, options) {
		if (options.text !== undefined) {
			if (element instanceof HTMLInputElement) {
				element.value = options.text;
				const label = element.querySelector(".js-addtocart-text");
				if (label) label.textContent = options.text;
			}
		}

		if (options.disabled !== undefined) {
			writeDisabled(element, options.disabled);
		}

		if (!(element instanceof HTMLElement)) return;
		if (options.fontColor !== undefined) {
			element.style.color = options.fontColor;
		}
		if (options.backgroundColor !== undefined) {
			element.style.backgroundColor = options.backgroundColor;
		}
		if (options.borderRadius !== undefined) {
			element.style.borderRadius = options.borderRadius;
		}
	},

	values(element) {
		const style = getComputedStyle(element);
		return {
			text: (element instanceof HTMLInputElement
				? element.value
				: element.textContent ?? ""
			).trim(),
			fontColor: style.color,
			// What the label sits on, not just the button's own background: a
			// theme that paints a wrapper and leaves the button transparent
			// would otherwise read as `rgba(0, 0, 0, 0)`. Also the colour the
			// `fontColor` contrast rule measures, so an app that derives its
			// colours from this reading passes the rule.
			backgroundColor: getEffectiveBackgroundColor(element),
			borderRadius: style.borderRadius,
			disabled: readDisabled(element),
		};
	},

	identify: formProductKey,

	validate(element, options) {
		if (options.text !== undefined && isEmptyString(options.text)) {
			return 'refused "text": a button label cannot be blank';
		}

		const refusal = colorPairRefusal(element, options);
		if (refusal) return refusal;

		if (options.borderRadius) {
			const refusal = borderRadiusRefusal(options.borderRadius);
			if (refusal) return `refused "borderRadius": ${refusal}`;
		}

		return null;
	},
};
